import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { User, Mail, Key, CreditCard, LogOut, Save, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { CreditPurchase } from '../components/payments/CreditPurchase';
import { getCreditBalance } from '../lib/api';
import { AuthForm } from '../components/auth/AuthForm';
export function Settings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  // Auth state
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // User data
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Credits
  const [credits, setCredits] = useState(0);
  const [loadingCredits, setLoadingCredits] = useState(true);
  useEffect(() => {
    checkAuthStatus();
  }, []);
  const checkAuthStatus = async () => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    if (user) {
      setIsAuthenticated(true);
      setEmail(user.email || '');
      loadCredits();
    } else {
      const guestMode = localStorage.getItem('guestMode') === 'true';
      setIsGuestMode(guestMode);
    }
  };
  const loadCredits = async () => {
    setLoadingCredits(true);
    try {
      const balance = await getCreditBalance();
      setCredits(balance);
    } catch (error) {
      console.error('Error loading credits:', error);
      setCredits(0);
    } finally {
      setLoadingCredits(false);
    }
  };
  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    if (newPassword !== confirmPassword) {
      setMessage({
        type: 'error',
        text: 'Passwords do not match'
      });
      setLoading(false);
      return;
    }
    if (newPassword.length < 6) {
      setMessage({
        type: 'error',
        text: 'Password must be at least 6 characters'
      });
      setLoading(false);
      return;
    }
    try {
      const {
        error
      } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) throw error;
      setMessage({
        type: 'success',
        text: 'Password updated successfully'
      });
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error updating password:', error);
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to update password'
      });
    } finally {
      setLoading(false);
    }
  };
  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  // Guest mode UI
  if (isGuestMode && !isAuthenticated) {
    return <div className="container py-8 max-w-md" data-id="element-2583">
        <div className="text-center mb-8" data-id="element-2584">
          <h1 className="text-3xl font-bold mb-2" data-id="element-2585">Create Your Account</h1>
          <p className="text-muted-foreground" data-id="element-2586">
            Sign up to get 5 free credits and save your transcriptions
          </p>
        </div>
        <Card data-id="element-2587">
          <CardContent className="p-6" data-id="element-2588">
            <AuthForm onLoginSuccess={() => {
            localStorage.removeItem('guestMode');
            window.location.reload();
          }} data-id="element-2589" />
          </CardContent>
        </Card>
      </div>;
  }
  // Authenticated user UI
  return <div className="container py-8 max-w-4xl" data-id="element-2590">
      <div className="mb-8" data-id="element-2591">
        <h1 className="text-3xl font-bold mb-2" data-id="element-2592">Settings</h1>
        <p className="text-muted-foreground" data-id="element-2593">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="space-y-6" data-id="element-2594">
        {/* Message */}
        {message && <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === 'success' ? 'bg-success/10 border border-success/20' : 'bg-destructive/10 border border-destructive/20'}`} data-id="element-2595">
            {message.type === 'success' ? <CheckCircle size={20} className="text-success" data-id="element-2596" /> : <AlertCircle size={20} className="text-destructive" data-id="element-2597" />}
            <p className={`text-sm ${message.type === 'success' ? 'text-success' : 'text-destructive'}`} data-id="element-2598">
              {message.text}
            </p>
          </div>}

        {/* Account Information */}
        <section data-id="element-2599">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2" data-id="element-2600">
            <User size={20} data-id="element-2601" />
            Account Information
          </h2>
          <Card data-id="element-2602">
            <CardContent className="p-6 space-y-4" data-id="element-2603">
              <div data-id="element-2604">
                <label className="block text-sm font-medium mb-2" data-id="element-2605">
                  Email Address
                </label>
                <div className="flex items-center gap-2" data-id="element-2606">
                  <Mail size={16} className="text-muted-foreground" data-id="element-2607" />
                  <Input type="email" value={email} disabled data-id="element-2608" />
                </div>
                <p className="text-xs text-muted-foreground mt-1" data-id="element-2609">
                  Contact support to change your email address
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Change Password */}
        <section data-id="element-2610">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2" data-id="element-2611">
            <Key size={20} data-id="element-2612" />
            Change Password
          </h2>
          <Card data-id="element-2613">
            <CardContent className="p-6" data-id="element-2614">
              <form onSubmit={handleUpdatePassword} className="space-y-4" data-id="element-2615">
                <div data-id="element-2616">
                  <label className="block text-sm font-medium mb-2" data-id="element-2617">
                    New Password
                  </label>
                  <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password" required data-id="element-2618" />
                </div>

                <div data-id="element-2619">
                  <label className="block text-sm font-medium mb-2" data-id="element-2620">
                    Confirm New Password
                  </label>
                  <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm new password" required data-id="element-2621" />
                </div>

                <Button type="submit" disabled={loading} data-id="element-2622">
                  {loading ? <>
                      <Loader2 size={16} className="mr-2 animate-spin" data-id="element-2623" />
                      Updating...
                    </> : <>
                      <Save size={16} className="mr-2" data-id="element-2624" />
                      Update Password
                    </>}
                </Button>
              </form>
            </CardContent>
          </Card>
        </section>

        {/* Credits & Billing */}
        <section data-id="element-2625">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2" data-id="element-2626">
            <CreditCard size={20} data-id="element-2627" />
            Credits & Billing
          </h2>
          <Card data-id="element-2628">
            <CardContent className="p-6" data-id="element-2629">
              {loadingCredits ? <div className="flex items-center justify-center py-8" data-id="element-2630">
                  <Loader2 size={24} className="animate-spin text-primary" data-id="element-2631" />
                </div> : <CreditPurchase currentCredits={credits} onPurchaseComplete={loadCredits} data-id="element-2632" />}
            </CardContent>
          </Card>
        </section>

        {/* Sign Out */}
        <section data-id="element-2633">
          <Card className="border-destructive/20" data-id="element-2634">
            <CardContent className="p-6" data-id="element-2635">
              <h3 className="font-semibold mb-2" data-id="element-2636">Sign Out</h3>
              <p className="text-sm text-muted-foreground mb-4" data-id="element-2637">
                Sign out of your account on this device
              </p>
              <Button variant="destructive" onClick={handleSignOut} data-id="element-2638">
                <LogOut size={16} className="mr-2" data-id="element-2639" />
                Sign Out
              </Button>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>;
}